import { toast } from 'react-toastify';
import axiosInstance from '../config/axiosConfig';
import { IUser } from '../interfaces/User';
import { Campaign } from '@/interfaces/campaign';
import { TokenStore } from './TokenStore';

export const login = async (email: string, password: string): Promise<any> => {
  try {
    const response = await axiosInstance.post('/auth/login', {
      email,
      password,
    });

    TokenStore.setAccessToken(response.data.accessToken);

    return response.data;
  } catch (error) {
    console.error('Error when logging in', error);
    toast.error('Login failed. Please check your credentials and try again.');
    return null;
  }
};

export const fetchUser = async (): Promise<any | null> => {
  try {
    console.log('Fetching user from /auth/me...');
    const response = await axiosInstance.get<IUser>('/auth/me');
    console.log('User data received:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error when fetching user', error);
    console.error('Error details:', error.response?.data || error.message);
    return null;
  }
};

export const fetchRoleInBrand = async (
  user: string,
  brand: string,
): Promise<any | null> => {
  try {
    const response = await axiosInstance.get('/auth/me/brandRole', {
      params: { user, brand },
    });

    return response.data;
  } catch (error) {
    console.error('Error when fetching user role', error);
    return null;
  }
};

export const fetchAllUsersAndLinks = async (params: any) => {
  try {
    const response = await axiosInstance.get('/users/fetchUsersWithLinks', {
      params,
    });

    return response.data;
  } catch (error) {
    console.error('Error when fetching users and links', error);
    return null;
  }
};

export const getAdminAnalytics = async () => {
  try {
    const response = await axiosInstance.get('/users/getAdminAnalytics');

    return response.data;
  } catch (error) {
    console.error('Error when fetching admin analytics', error);
    return null;
  }
};

export const UserProfile = async (userId: string) => {
  try {
    const response = await axiosInstance.get(`/users/${userId}`);

    return response.data;
  } catch (error) {
    console.error('Error when getting user', error);
    return null;
  }
};
export const whitelistUser = async (userId: string) => {
  try {
    const response = await axiosInstance.get(`/users/${userId}/whitelist`);

    return response.data;
  } catch (error) {
    console.error('Error when getting whitelist user', error);
    return null;
  }
};
export const deleteUser = async (userId: string) => {
  try {
    const response = await axiosInstance.delete(`/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error when deleting user', error);
    return null;
  }
};

export const UserUpdate = async (userId: string, values: any) => {
  try {
    const response = await axiosInstance.put(`/users/${userId}`, values);

    return response.data;
  } catch (error) {
    console.error('Error when fetching user', error);
    return null;
  }
};

export const UserPassword = async (userId: string, values: any) => {
  try {
    await axiosInstance.put(`/users/${userId}/password`, values);
    return true;
  } catch (error) {
    console.error('Error when updating password', error);
    throw error;
  }
};

export const ProfileCompletion = async (userId: string) => {
  try {
    const response = await axiosInstance.get(
      `/users/${userId}/profile-completion`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when fetching user profile completion', error);
    return null;
  }
};

export const addUserToBrand = async (
  userId: string,
  brandId: string,
  name: string,
  email: string,
  role: string,
) => {
  try {
    const response = await axiosInstance.post(
      `/users/${userId}/brand/${brandId}/invite`,
      {
        name,
        email,
        role,
      },
    );

    return response.data;
  } catch (error: any) {
    console.error('Error when adding user to brand', error);
    throw new Error(error.response.data.message || 'Something went wrong');
  }
};

export const reInviteUserToBrand = async (
  userId: string,
  brandId: string,
  email: string,
) => {
  try {
    const response = await axiosInstance.post(
      `/users/${userId}/brand/${brandId}/re-invite`,
      {
        email,
      },
    );

    return response.data;
  } catch (error) {
    console.error('Error when re-inviting user to brand', error);
    return null;
  }
};

export const activateBrandUser = async (
  token: string,
  userId: string,
  password: string,
) => {
  try {
    const response = await axiosInstance.put(
      `/users/${userId}/brand/activate`,
      {
        token,
        password,
      },
    );

    return response.data;
  } catch (error) {
    console.error('Error when activating user', error);
    return null;
  }
};

export const deactivateBrandUser = async (userId: string) => {
  try {
    const response = await axiosInstance.put(
      `/users/${userId}/brand/deactivate`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when deactivating user', error);
    return null;
  }
};

export const reactivateBrandUser = async (userId: string) => {
  try {
    const response = await axiosInstance.put(
      `/users/${userId}/brand/reactivate`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when reactivating user', error);
    return null;
  }
};

export const deleteBrandUser = async (userId: string) => {
  try {
    const response = await axiosInstance.put(
      `/users/${userId}/brand/delete-user`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when deleting user', error);
    return null;
  }
};

export const updateBrandUserRole = async (userId: string, role: string) => {
  try {
    const response = await axiosInstance.put(
      `/users/${userId}/brand/edit-role`,
      {
        role,
      },
    );

    return response.data;
  } catch (error) {
    console.error('Error when updating brand user', error);
    return null;
  }
};

export const getBrandUsers = async (
  userId: string,
  brandId: string,
  page: number,
  limit: number,
  searchTerm?: string,
) => {
  try {
    const response = await axiosInstance.get(
      `/users/${userId}/brand/${brandId}/users?page=${page}&limit=${limit}&searchTerm=${searchTerm}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error when fetching brand users', error);
    return null;
  }
};

export const getBrand = async (userId: string, brandId: string) => {
  try {
    const response = await axiosInstance.get(
      `/users/${userId}/brand/${brandId}`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when fetching brand', error);
    return null;
  }
};

export const updateBrand = async (
  userId: string,
  brandId: string,
  params: any,
) => {
  try {
    const response = await axiosInstance.put(
      `/users/${userId}/brand/${brandId}`,
      params,
    );

    return response.data;
  } catch (error) {
    console.error('Error when updating brand', error);
    return null;
  }
};

export const updateUserProfile = async (formData: FormData) => {
  try {
    const response = await axiosInstance.post(`/users/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  } catch (error) {
    console.error('Error when updating user', error);
    return null;
  }
};

export const updateUserYoutubeTokens = async (code: string) => {
  try {
    const response = await axiosInstance.post(`/users/youtube-access`, {
      code,
    });

    return response.data;
  } catch (error) {
    console.error('Error when updating user youtube tokens', error);
    return null;
  }
};

export const removeUserYoutubeTokens = async () => {
  try {
    const response = await axiosInstance.post(`/users/youtube-disconnect`);

    return response.data;
  } catch (error) {
    console.error('Error when removing user youtube tokens', error);
    return null;
  }
};
export const removeUserInstaTokens = async (userId: string) => {
  try {
    const response = await axiosInstance.post(
      `/users/instagram-disconnect/app`,
      {
        userId,
      },
    );

    return response.data;
  } catch (error) {
    console.error('Error when removing user youtube tokens', error);
    return null;
  }
};
export const removeUserTiktokTokens = async (userId: string) => {
  try {
    const response = await axiosInstance.post(`/users/tiktok-disconnect/app`, {
      userId,
    });

    return response.data;
  } catch (error) {
    console.error('Error when removing user youtube tokens', error);
    return null;
  }
};

/**
 * Fetches a single-use CSRF state to embed in the Instagram authorize URL. The
 * callback spends it, so a missing, replayed or expired state is rejected 400.
 */
export const getInstagramOAuthState = async (): Promise<string | null> => {
  try {
    const response = await axiosInstance.get('/auth/instagram/oauth-state');
    return response.data?.state ?? null;
  } catch (error) {
    console.error('Error fetching Instagram OAuth state', error);
    return null;
  }
};

// The backend derives the user from the auth token, never a ?userId= param.
// Rethrows rather than returning null: swallowing the error resolved the
// mutation with null, so onSuccess ran and `res.updatedUser` threw — and the
// 409 "already connected to another profile" message never reached the user.
export const updateUserInstagramTokens = async (
  code: string,
  state: string,
) => {
  const response = await axiosInstance.get(
    `/auth/callback/instagram/web?code=${encodeURIComponent(
      code,
    )}&state=${encodeURIComponent(state)}`,
  );

  return response.data;
};

export const updateUserTiktokTokens = async (code: any, userId: string) => {
  try {
    const response = await axiosInstance.get(
      `/auth/callback/tiktok/web?code=${encodeURIComponent(
        code,
      )}&userId=${encodeURIComponent(userId)}`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when updating user tiktok tokens', error);
    return null;
  }
};
export const getBrandDeposits = async (
  brandId: string,
  page: number,
  limit: number,
) => {
  try {
    const response = await axiosInstance.get(
      `/deposit/brand/${brandId}/get-deposit?page=${page}&limit=${limit}`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when fetching brand deposits', error);
    return null;
  }
};

export const getBrandTotals = async (brandId: string) => {
  try {
    const response = await axiosInstance.get(
      `/deposit/brand/${brandId}/get-totals`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when fetching brand totals', error);
    return null;
  }
};

export const getBrandCampaigns = async (
  status: string,
  page: number,
  limit: number,
) => {
  try {
    const response = await axiosInstance.get(
      `/campaign/get-campaigns?page=${page}&limit=${limit}&status=${status}`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when fetching brand totals', error);
    return null;
  }
};

export const getBrandCampaignById = async (campaignId: string) => {
  try {
    const response = await axiosInstance.get(
      `/campaign/get-campaign/${campaignId}`,
    );

    return response.data;
  } catch (error) {
    console.error('Error when fetching brand totals', error);
    return null;
  }
};

export const createBrandCampaign = async (CampaignData: Campaign) => {
  try {
    const response = await axiosInstance.post(`/campaign/create`, {
      ...CampaignData,
    });

    return response.data;
  } catch (error) {
    console.error('Error when fetching brand totals', error.response?.data);
    toast.error(
      error.response?.data?.message ||
        'An error occurred while creating the campaign',
    );
    return null;
  }
};

export const uploadBrandImageToS3 = async (image: File) => {
  try {
    const response = await axiosInstance.postForm(`/campaign/upload`, {
      file: image,
    });

    return response.data;
  } catch (error) {
    console.error('Error when fetching brand totals', error);
    return null;
  }
};
export const uploadBrandLogoToS3 = async (image: File) => {
  try {
    const response = await axiosInstance.postForm(`/brand/uploadBrandLogo`, {
      file: image,
    });

    return response.data;
  } catch (error) {
    console.error('Error when fetching brand totals', error);
    return null;
  }
};
export const fetchLocation = async (keyword: string, areaType?: string) => {
  try {
    const response = await axiosInstance.get(
      `/auth/fetch-location?keyword=${keyword}${areaType !== 'store' ? `&type=${areaType}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error when fetching brand totals', error);
    return null;
  }
};

export const updateBrandCampaign = async (
  id: string,
  updateData: Partial<Campaign>,
) => {
  try {
    const response = await axiosInstance.patch(`/campaign/update/${id}`, {
      ...updateData,
    });
    return response.data;
  } catch (error) {
    toast.error(
      error.response?.data?.message ||
        'An error occurred while updating the campaign',
    );
    console.error('Error when fetching brand totals', error);
    return null;
  }
};

export const reRunCampaign = async (id: string) => {
  try {
    const response = await axiosInstance.post(`/campaign/recreate/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error when fetching brand totals', error);
    return null;
  }
};

export const getCampaignStatsByBrand = async (brandId: string) => {
  try {
    const response = await axiosInstance.get(`/campaign/stats/${brandId}`);
    return response.data;
  } catch (error) {
    console.error('Error when fetching campaign stats', error);
    return null;
  }
};
export const deleteDraftCampaign = async (draftId: string) => {
  try {
    const response = await axiosInstance.post('/campaign/deleteDraft', {
      id: draftId,
    });

    return response.data;
  } catch (error: any) {
    console.error('Error when deleting draft campaign', error);
    throw error;
  }
};
